import { createContext, useContext, ReactNode, useState } from 'react';
import {
  EntityCategory,
  selectExistingColumnVisible,
  useDetectedEntitiesStore,
} from '../stores/useDetectedEntitiesStore';

interface EntityLayoutContextValue {
  titleWidth: number;
  registerTitleWidth: (width: number) => void;
}

const EntityLayoutContext = createContext<EntityLayoutContextValue | undefined>(undefined);
const MIN_TITLE_WIDTH_FOUR_COLS = 150;
const MIN_TITLE_WIDTH_THREE_COLS = 220;

interface EntityLayoutProviderProps {
  children: ReactNode;
  entityType: string;
  category: EntityCategory;
}

export function EntityLayoutProvider({
  children,
  entityType,
  category,
}: EntityLayoutProviderProps) {
  const isExistingColVisible = useDetectedEntitiesStore(state =>
    selectExistingColumnVisible(state, category, entityType),
  );
  const minWidth = isExistingColVisible ? MIN_TITLE_WIDTH_FOUR_COLS : MIN_TITLE_WIDTH_THREE_COLS;
  const [titleWidth, setTitleWidth] = useState(minWidth);

  const registerTitleWidth = (width: number) => {
    if (width !== titleWidth) {
      setTitleWidth(Math.max(width, minWidth));
    }
  };

  const value = {
    titleWidth,
    registerTitleWidth,
  };

  return <EntityLayoutContext.Provider value={value}>{children}</EntityLayoutContext.Provider>;
}

export function useEntityLayout() {
  const context = useContext(EntityLayoutContext);
  if (context === undefined) {
    throw new Error('useEntityLayout must be used within an EntityLayoutProvider');
  }
  return context;
}
